"use client"
import { useState } from "react"
import Link from "next/link"
import { supplementsManifest } from "@/content/markdown/supplements/manifest"
import { SUPPLEMENT_PREVIEWS } from "@/content/markdown/supplements/previews"
import { SUPPLEMENT_SECTIONS, SUPPLEMENT_SUBTITLES } from "@/app/supplements/sections"

// Bespoke supplements that live outside the gdoc manifest (hand-maintained
// under content/non-gdoc-supplements/ or standalone tools), rendered through
// their own routes. Keyed by slug so a bespoke route overrides a stale
// manifest twin of the same slug rather than listing twice.
const BESPOKE_SUPPLEMENT_TITLES: Record<string, string> = {
  "verification-plan": "Verification Plan",
  "security-in-plan-a": "Security in Plan A",
  "economics-of-plan-a": "Economics of Plan A",
  "compute-supplement": "Compute in Plan A",
  "econ-explorer": "Economic Growth Explorer",
}

type SupplementsListProps = {
  className?: string
  showPreview?: boolean
}

type HoverState = { slug: string; top: number; title: string }

export const SupplementsList = ({
  className = "no-underline hover:font-semibold hover:text-[#8b0000]",
  showPreview = false,
}: SupplementsListProps) => {
  const [hover, setHover] = useState<HoverState | null>(null)
  const preview = hover ? SUPPLEMENT_PREVIEWS[hover.slug] : null
  const oneLiner = hover ? SUPPLEMENT_SUBTITLES[hover.slug] ?? preview?.paragraph : null

  const titlesBySlug: Record<string, string> = {}
  for (const entry of supplementsManifest.entries) {
    titlesBySlug[entry.slug] = (entry.title.split("[Apr] ")[1] || entry.title).trim()
  }
  Object.assign(titlesBySlug, BESPOKE_SUPPLEMENT_TITLES)

  // Group the links by the same sections as the /supplements index page. Any
  // supplement not assigned to a section still renders — under "Other" — so a
  // newly-added one can never silently disappear from the menu.
  const sectionedSlugs = new Set(SUPPLEMENT_SECTIONS.flatMap((section) => section.slugs))
  const sections = SUPPLEMENT_SECTIONS.map((section) => ({
    title: section.title,
    slugs: section.slugs.filter((slug) => titlesBySlug[slug]),
  }))
  const leftoverSlugs = Object.keys(titlesBySlug).filter((slug) => !sectionedSlugs.has(slug))
  if (leftoverSlugs.length > 0) sections.push({ title: "Other", slugs: leftoverSlugs })

  const handleEnter = (slug: string, title: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    const link = event.currentTarget
    const parent = link.offsetParent as HTMLElement | null
    const top = parent ? link.offsetTop : 0
    setHover({ slug, top, title })
  }

  return (
    <>
      {sections.map((section) => (
        <div key={section.title} className="flex flex-col">
          {/* Same section-tag voice as the /supplements index page (SectionTag):
              red mono small-caps — the two-tone palette's label ink. */}
          <div className="pt-3 pb-1 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.1em] text-[#8b0000] whitespace-nowrap">
            {section.title}
          </div>
          {section.slugs.map((slug) => {
            const displayTitle = titlesBySlug[slug]
            return (
              <Link
                key={slug}
                className={className}
                href={`/supplements/${slug}`}
                onMouseEnter={showPreview ? handleEnter(slug, displayTitle) : undefined}
                onMouseLeave={showPreview ? () => setHover(null) : undefined}
              >
                {displayTitle}
                {/* Zero-height bold twin reserves the hovered (bold) width up
                    front, so bolding on hover never widens the row and makes
                    the dropdown jitter. */}
                <span aria-hidden="true" className="block h-0 overflow-hidden invisible font-semibold">
                  {displayTitle}
                </span>
              </Link>
            )
          })}
        </div>
      ))}
      {showPreview && hover && oneLiner && (
        <div
          className="hidden md:block absolute right-full mr-2 w-96 max-w-[calc(100vw-2rem)] bg-[#fffff8] border border-black rounded-sm px-3 py-3 font-serif text-base text-black leading-snug whitespace-normal pointer-events-none"
          style={{ top: hover.top }}
        >
          <div className="font-serif text-lg text-black mb-3">
            {hover.title}
          </div>
          {oneLiner}
          {preview && preview.headings.length > 0 && (
            <ul className="font-sans mt-3 pt-2 border-t border-black flex flex-col gap-2 text-sm text-black text-balance p-0 pl-6">
              {preview.headings.map((heading, headingIndex) => (
                <li className="list-disc" key={headingIndex}>{heading.text}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  )
}
